// src/components/sops/SopReplaceFileModal.js — sopweb-bdb7afcd (SOPs Library, Seam 2)
// Replace file: a new PDF becomes the next version (older versions are kept). "What changed?" note
// (optional) and "Notify the N people who can see it" (on by default). The tick is STORED on the
// version (finishSopUpload notifyPeople); sending the notification is built in Seam 4.
import React, { useState } from 'react';
import { FileText } from 'lucide-react';
import { Button, FieldLabel, Modal, Notice, inputClass } from './SopUi';
import { replaceSopFile, friendlyError } from './sopsApi';
import { LIMITS, formatBytes, pdfFileProblem, looksLikePdf } from '../../utils/sopsUi';

export default function SopReplaceFileModal({ sop, onClose, onDone }) {
    const [file, setFile] = useState(null);
    const [problem, setProblem] = useState(null);
    const [note, setNote] = useState('');
    const [notify, setNotify] = useState(true);
    const [saving, setSaving] = useState(null);
    const [error, setError] = useState('');
    const people = typeof sop.canSeeCount === 'number' ? sop.canSeeCount : 0;

    const pick = async (f) => {
        setFile(f || null); setError('');
        if (!f) { setProblem(null); return; }
        const p = pdfFileProblem(f);
        if (p) { setProblem(p); return; }
        try { setProblem(looksLikePdf(new Uint8Array(await f.slice(0, 5).arrayBuffer())) ? null : 'This file isn’t a real PDF (it may just be renamed). Save it as a PDF and choose it again.'); }
        catch (e) { setProblem('This file couldn’t be read. Choose it again.'); }
    };

    const save = async () => {
        if (!file) { setError('Choose the new PDF file.'); return; }
        if (problem) { setError(problem); return; }
        setError('');
        setSaving({ step: 'upload', fraction: 0 });
        try {
            const res = await replaceSopFile(file, sop.id, { changeNote: note.trim(), notifyPeople: notify }, (step, fraction) => setSaving({ step, fraction }));
            onDone(res);
        } catch (e) {
            console.warn('sopweb: replace file failed', e);
            setSaving(null);
            setError(friendlyError(e, 'The new file couldn’t be saved. Please try again.'));
        }
    };

    const busy = !!saving;
    return (
        <Modal title="Replace file" wide onClose={busy ? () => {} : onClose}
            footer={<>
                <span className="mr-auto self-center text-sm text-gray-600" aria-live="polite">
                    {saving ? (saving.step === 'upload' ? `Uploading… ${Math.round((saving.fraction || 0) * 100)}%` : 'Checking the file…') : ''}
                </span>
                <Button onClick={onClose} disabled={busy}>Cancel</Button>
                <Button variant="primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : `Save as version ${(sop.currentVersionNumber || 1) + 1}`}</Button>
            </>}>
            <p className="text-sm text-gray-600">The new file becomes version {(sop.currentVersionNumber || 1) + 1}. Older versions are kept, and Managers and up can still open them.</p>
            <div>
                <FieldLabel htmlFor="sop-new-file" required hint="PDF only, up to 25 MB.">New PDF</FieldLabel>
                <input id="sop-new-file" type="file" accept="application/pdf,.pdf" disabled={busy} onChange={(e) => pick(e.target.files && e.target.files[0])}
                    className="block w-full text-sm file:mr-3 file:rounded-lg file:border-0 file:bg-[#5F4EC9]/10 file:px-3 file:py-2 file:text-[#5F4EC9] file:font-medium" />
                {file && !problem && <p className="mt-2 flex items-center gap-2 text-sm"><FileText size={16} className="text-[#5F4EC9]" /> {file.name} · {formatBytes(file.size)}</p>}
                {problem && <div className="mt-2"><Notice tone="error">{problem}</Notice></div>}
            </div>
            <div>
                <FieldLabel htmlFor="sop-change-note" hint="People will see this with the new version.">What changed?</FieldLabel>
                <textarea id="sop-change-note" rows={3} className={inputClass} value={note} maxLength={LIMITS.CHANGE_NOTE_MAX} disabled={busy} onChange={(e) => setNote(e.target.value)} />
            </div>
            <div>
                <label className="flex items-start gap-2 text-sm text-[#12192D] cursor-pointer">
                    <input type="checkbox" checked={notify} disabled={busy} onChange={(e) => setNotify(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#5F4EC9]" />
                    <span>Notify the {people} {people === 1 ? 'person' : 'people'} who can see it <span className="text-xs text-gray-500">Optional</span>
                        <span className="block text-xs text-gray-500">Your choice is saved with this version. Spatchy starts sending these in a later update.</span></span>
                </label>
            </div>
            {error && <Notice tone="error">{error}</Notice>}
        </Modal>
    );
}
